import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { CalendarBlank, Plus, Trash, ClockCounterClockwise, ArrowLeft, ArrowRight, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DatePickerZoom from "@/components/DatePickerZoom";
import PatientSearchSelect from "@/components/PatientSearchSelect";

const STATUS_COLORS = {
  scheduled: "bg-pf-primary-light text-pf-primary",
  completed: "bg-pf-success-bg text-pf-success",
  canceled: "bg-pf-danger-bg text-pf-danger",
  no_show: "bg-pf-warning-bg text-pf-warning",
};

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Monday = 0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}

export default function AppointmentsPage() {
  const { t, lang } = useLang();
  const locale = lang === "ro" ? "ro-RO" : "en-US";
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [appts, setAppts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null); // appointment obj or null → creating
  const [patients, setPatients] = useState([]);
  const [vets, setVets] = useState([]);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(anchor);
      d.setDate(anchor.getDate() + i);
      return d;
    });
  }, [anchor]);

  const weekEnd = useMemo(() => new Date(anchor.getTime() + 6 * 86400000), [anchor]);

  async function load() {
    try {
      const from = anchor.toISOString().slice(0, 10);
      const to = new Date(anchor.getTime() + 7 * 86400000).toISOString().slice(0, 10);
      const [aRes, pRes, uRes] = await Promise.all([
        api.get("/appointments", { params: { from_date: from, to_date: to } }),
        api.get("/patients", { params: { limit: 1000 } }),
        api.get("/users"),
      ]);
      setAppts(aRes.data);
      setPatients(pRes.data);
      setVets(uRes.data.filter((u) => u.role !== "assistant"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [anchor]);

  function apptsForDay(d) {
    const iso = d.toISOString().slice(0, 10);
    return appts
      .filter((a) => (a.start_at || "").slice(0, 10) === iso)
      .sort((a, b) => (a.start_at > b.start_at ? 1 : -1));
  }

  async function del(id) {
    if (!window.confirm(t("delete") + "?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function updateStatus(id, status) {
    try {
      await api.patch(`/appointments/${id}`, { status });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function openNew() {
    setEditing(null);
    setOpenDialog(true);
  }

  function openEdit(a, e) {
    if (e) e.stopPropagation();
    setEditing(a);
    setOpenDialog(true);
  }

  // Fast week jump: pick any date from the calendar and snap to that week
  function jumpTo(isoDate) {
    if (!isoDate) return;
    const d = new Date(isoDate);
    if (!isNaN(d.getTime())) setAnchor(startOfWeek(d));
  }

  const weekLabel = `${t("week")} ${anchor.toLocaleDateString(locale, { day: "numeric", month: "short" })} — ${weekEnd.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-extrabold text-pf-text">{t("appointments")}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setAnchor(new Date(anchor.getTime() - 7 * 86400000))}
            className="pf-btn-ghost"
            data-testid="prev-week-btn"
          >
            <ArrowLeft size={14} />
          </button>
          <button onClick={() => setAnchor(startOfWeek(new Date()))} className="pf-btn-ghost" data-testid="today-btn">
            {t("today")}
          </button>
          <button
            onClick={() => setAnchor(new Date(anchor.getTime() + 7 * 86400000))}
            className="pf-btn-ghost"
            data-testid="next-week-btn"
          >
            <ArrowRight size={14} />
          </button>
          <button onClick={openNew} className="pf-btn-primary" data-testid="new-appointment-btn">
            <Plus size={14} weight="bold" /> {t("newAppointment")}
          </button>
        </div>
      </div>

      <div className="pf-card p-3 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-pf-text font-semibold" data-testid="week-label">
          <CalendarBlank size={16} className="text-pf-primary" />
          {weekLabel}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="pf-label">{t("changeDate")}:</span>
          <div className="w-44">
            <DatePickerZoom
              testId="jump-to-date"
              value=""
              onChange={jumpTo}
              placeholder={t("changeDate")}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map((d) => {
          const rows = apptsForDay(d);
          const isToday = d.toDateString() === new Date().toDateString();
          return (
            <div
              key={d.toISOString()}
              className={`pf-card p-3 min-h-[240px] ${isToday ? "ring-2 ring-pf-primary/40" : ""}`}
              data-testid={`day-${d.toISOString().slice(0,10)}`}
            >
              <div className="mb-2">
                <div className="pf-label">{d.toLocaleDateString(locale, { weekday: "short" })}</div>
                <div className={`font-display font-bold text-xl ${isToday ? "text-pf-primary" : "text-pf-text"}`}>
                  {d.getDate()}
                </div>
              </div>
              {rows.length === 0 && <div className="text-xs text-pf-text-secondary italic">{t("noAppointments")}</div>}
              <div className="space-y-2">
                {rows.map((a) => {
                  const time = new Date(a.start_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
                  const statusLabel = {
                    scheduled: t("apptScheduled"),
                    completed: t("completed"),
                    canceled: t("apptCanceled"),
                    no_show: t("noShow"),
                  }[a.status] || a.status;
                  return (
                    <div
                      key={a.id}
                      data-testid={`appointment-${a.id}`}
                      className="rounded-lg border border-pf-border bg-white p-2.5 hover:shadow-card transition-shadow"
                    >
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <span className="text-xs font-mono text-pf-text-secondary flex items-center gap-1">
                          <ClockCounterClockwise size={11} /> {time}
                        </span>
                        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${STATUS_COLORS[a.status] || "bg-pf-bg text-pf-text-secondary"}`}>
                          {statusLabel}
                        </span>
                      </div>
                      <Link to={`/patients/${a.patient_id}`} className="text-sm font-semibold text-pf-text hover:text-pf-primary">
                        {a.patient_name || "—"}
                      </Link>
                      {a.reason && <div className="text-xs text-pf-text-secondary truncate">{a.reason}</div>}
                      <div className="text-xs text-pf-text-secondary truncate">{a.attending_vet_name || ""}</div>
                      <div className="flex gap-1 mt-2">
                        {a.status === "scheduled" && (
                          <>
                            <button
                              onClick={() => updateStatus(a.id, "completed")}
                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-pf-success-bg text-pf-success hover:brightness-95"
                            >
                              {t("completed")}
                            </button>
                            <button
                              onClick={() => updateStatus(a.id, "no_show")}
                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-pf-warning-bg text-pf-warning hover:brightness-95"
                            >
                              {t("noShow")}
                            </button>
                          </>
                        )}
                        <button
                          onClick={(e) => openEdit(a, e)}
                          className="ml-auto text-pf-text-secondary hover:text-pf-primary"
                          title={t("editAppointment")}
                          data-testid={`appt-edit-${a.id}`}
                        >
                          <PencilSimple size={12} />
                        </button>
                        <button onClick={() => del(a.id)} className="text-pf-text-secondary hover:text-pf-danger">
                          <Trash size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <AppointmentDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        patients={patients}
        vets={vets}
        editing={editing}
        onSaved={() => {
          setOpenDialog(false);
          setEditing(null);
          load();
        }}
      />
    </div>
  );
}

function AppointmentDialog({ open, onOpenChange, patients, vets, editing, onSaved }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isEdit = !!editing;
  const [form, setForm] = useState({
    patient_id: "",
    start_at: "",
    title: "",
    reason: "",
    attending_vet_id: "",
    status: "scheduled",
    reminder_enabled: false,
    reminder_lang: "ro",
  });
  const [conflict, setConflict] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setConflict(null);
      return;
    }
    if (editing) {
      // Normalise ISO → HTML input local string
      const start = editing.start_at
        ? new Date(editing.start_at).toISOString().slice(0, 16)
        : "";
      setForm({
        patient_id: editing.patient_id || "",
        start_at: start,
        title: editing.title || "",
        reason: editing.reason || "",
        attending_vet_id: editing.attending_vet_id || "",
        status: editing.status || "scheduled",
        reminder_enabled: editing.reminder_enabled === true,
        reminder_lang: editing.reminder_lang || lang || "ro",
      });
    } else {
      setForm({
        patient_id: "",
        start_at: "",
        title: "",
        reason: "",
        attending_vet_id: "",
        status: "scheduled",
        reminder_enabled: false,          // OFF by default
        reminder_lang: lang || "ro",       // snapshot the language at creation time
      });
    }
  }, [open, editing, lang]);

  async function submit(e, forceOverride = false) {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.patient_id || !form.start_at) {
      toast.error(t("required"));
      return;
    }
    setBusy(true);
    if (!forceOverride) setConflict(null);
    try {
      const payload = {
        patient_id: form.patient_id,
        start_at: new Date(form.start_at).toISOString(),
        title: form.title,
        reason: form.reason,
        attending_vet_id: form.attending_vet_id || null,
        override_conflict: forceOverride,
        reminder_enabled: !!form.reminder_enabled,
        reminder_lang: form.reminder_lang || "ro",
      };
      if (isEdit) {
        payload.status = form.status;
        await api.patch(`/appointments/${editing.id}`, payload);
        toast.success(t("saved"));
      } else {
        await api.post("/appointments", payload);
        toast.success(t("saved"));
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "appointment_conflict") {
        setConflict(detail);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1rem)] max-h-[92dvh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? t("editAppointment") : t("newAppointment")}
          </DialogTitle>
        </DialogHeader>
        {conflict && (
          <div data-testid="appt-conflict-banner" className="rounded-lg border border-pf-danger/30 bg-pf-danger-bg p-3 text-sm">
            <div className="font-semibold text-pf-danger mb-1">{t("conflictDetected")}</div>
            <div className="text-pf-text">{conflict.message}</div>
            <div className="mt-2 text-xs text-pf-text-secondary">
              <span className="font-medium">{conflict.conflict?.patient_name || "—"}</span>
              {conflict.conflict?.owner_name && <> · {conflict.conflict.owner_name}</>}
              {conflict.conflict?.reason && <> · {conflict.conflict.reason}</>}
            </div>
            {conflict.override_available && (
              <div className="mt-2">
                <button
                  type="button"
                  data-testid="appt-override-btn"
                  onClick={() => submit(null, true)}
                  disabled={busy}
                  className="text-xs font-semibold px-2 py-1 rounded bg-pf-warning text-white hover:brightness-95"
                >
                  {t("overrideConflict")}
                </button>
              </div>
            )}
          </div>
        )}
        <form onSubmit={(e) => submit(e, false)} className="space-y-3">
          <div>
            <label className="pf-label mb-1 block">{t("patient")}</label>
            <PatientSearchSelect
              testId="appt-patient"
              patients={patients}
              value={form.patient_id}
              onChange={(id) => setForm({ ...form, patient_id: id })}
              required
              placeholder={t("searchPlaceholder")}
            />
          </div>
          <div>
            <label className="pf-label mb-1 block">{t("dateTime")}</label>
            <DatePickerZoom
              testId="appt-time"
              withTime
              value={form.start_at}
              onChange={(v) => {
                setForm({ ...form, start_at: v });
                setConflict(null);
              }}
            />
          </div>
          <div>
            <label className="pf-label mb-1 block">{t("reason")}</label>
            <input
              data-testid="appt-reason"
              className="pf-input"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </div>
          <div>
            <label className="pf-label mb-1 block">{t("attendingVet")}</label>
            <select
              data-testid="appt-vet"
              className="pf-input"
              value={form.attending_vet_id}
              onChange={(e) => {
                setForm({ ...form, attending_vet_id: e.target.value });
                setConflict(null);
              }}
            >
              <option value="">—</option>
              {vets.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>
          {isEdit && (
            <div>
              <label className="pf-label mb-1 block">{t("invoiceStatus")}</label>
              <select
                data-testid="appt-status"
                className="pf-input"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="scheduled">{t("apptScheduled")}</option>
                <option value="completed">{t("completed")}</option>
                <option value="canceled">{t("apptCanceled")}</option>
                <option value="no_show">{t("noShow")}</option>
              </select>
            </div>
          )}
          <div className="rounded-lg border border-pf-border p-3 bg-pf-bg/40">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="appt-reminder-toggle"
                checked={!!form.reminder_enabled}
                onChange={(e) => setForm({ ...form, reminder_enabled: e.target.checked })}
                className="h-4 w-4 rounded border-pf-border text-pf-primary focus:ring-pf-primary"
              />
              <span className="text-sm font-medium text-pf-text">{t("apptEnableReminder")}</span>
            </label>
            {form.reminder_enabled && (
              <div className="mt-2 text-xs text-pf-text-secondary">
                {t("apptReminderLangHint")}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="pf-btn-ghost">
              {t("cancel")}
            </button>
            <button data-testid="appt-submit" disabled={busy} type="submit" className="pf-btn-primary">
              {busy ? "…" : isEdit ? t("saveChanges") : t("schedule")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
